import React, { useState, useEffect } from "react";
import Loading from "../Components/Loading";
import Error from "../Components/Error";
import dayjs from "dayjs";
import axios from "axios";

const BASE_URL = "https://erelatevmdev.centralindia.cloudapp.azure.com/api";

const TeamSummaryReport_BusinessActivities = () => {
  const [period, setPeriod] = useState("Today");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Calculate date range based on selected period
  const calculateDateRange = (selectedPeriod) => {
    const today = dayjs();
    let startDate, endDate;

    switch (selectedPeriod) {
      case "Today":
        startDate = today;
        endDate = today;
        break;
      case "Yesterday":
        startDate = today.subtract(1, "day");
        endDate = today.subtract(1, "day");
        break;
      case "ThisWeek":
        startDate = today.startOf("week");
        endDate = today;
        break;
      case "Last15Days":
        startDate = today.subtract(15, "day");
        endDate = today;
        break;
      case "CurrentMonth":
        startDate = today.startOf("month");
        endDate = today;
        break;
      case "Last1Month":
        startDate = today.subtract(1, "month");
        endDate = today;
        break;
      default:
        startDate = today;
        endDate = today;
    }

    return {
      start: startDate.format("YYYY-MM-DD"),
      end: endDate.format("YYYY-MM-DD"),
    };
  };

  // Fetch data when period changes
  useEffect(() => {
    const fetchData = () => {
      setIsLoaded(false);
      setError(null);

      const { start, end } = calculateDateRange(period);

      axios
        .get(
          `${BASE_URL}/Account/GetSalesTeamMember_AccountsSummary/${start}/${end}`,
          {
            headers: {
              "Cache-Control": "no-cache",
              "Access-Control-Allow-Origin": "*",
              Authorization: localStorage.getItem("loginToken"),
            },
          }
        )
        .then((response) => {
          setData(response.data);
          setIsLoaded(true);
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            window.location.replace("/Logout");
          } else {
            setError(err);
            setIsLoaded(true);
          }
        });
    };

    fetchData();
  }, [period]);

  if (!isLoaded) return <Loading />;
  if (error) return <Error data={error.message || error.toString()} />;

  if (!data)
    return (
      <p className="text-center text-muted mt-4">
        No data available for the selected period.
      </p>
    );

  // Destructure summaries from data.result
  const {
    orderSummary = [],
    invoicesSummary = [],
    reciptsCollectionSummary = [],
  } = data.result || {};

  // Calculate totals for Orders Summary
  const totalOrdersCount = orderSummary.reduce(
    (sum, item) => sum + (item.orderCount || 0),
    0
  );
  const totalOrdersValue = orderSummary.reduce(
    (sum, item) => sum + (item.totalOrderValue || 0),
    0
  );

  // Calculate total for Invoices Summary
  const totalInvoiceAmount = invoicesSummary.reduce(
    (sum, item) => sum + (item.totalAmount || 0),
    0
  );

  // Calculate total for Receipts Summary
  const totalReceiptsAmount = reciptsCollectionSummary.reduce(
    (sum, item) => sum + (item.totalAmount || 0),
    0
  );

  return (
    <section className="content">
      <div className="container-fluid px-2 px-md-4">
        {/* ===== Page Header & Period Filter ===== */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="fw-bold text-primary m-0">
            <i className="fa fa-users me-2"></i>
            Team Members Business Activities
          </h3>

          <select
            className="form-select w-auto"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="ThisWeek">This Week</option>
            <option value="Last15Days">Last 15 Days</option>
            <option value="CurrentMonth">Current Month</option>
            <option value="Last1Month">Last 1 Month</option>
          </select>
        </div>

        {/* ===== Orders Summary ===== */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-info text-white">
            <h5 className="card-title mb-0">1. Orders Summary</h5>
          </div>
          <div className="card-body table-responsive p-2">
            {orderSummary.length === 0 ? (
              <p className="text-muted text-center m-0">
                No order summary data found.
              </p>
            ) : (
              <table className="table table-bordered table-hover table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Company</th>
                    <th>Status</th>
                    <th className="text-center">Order Count</th>
                    <th className="text-center">Total Order Value</th>
                  </tr>
                </thead>
                <tbody>
                  {orderSummary.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.companyName}</td>
                      <td>{item.status}</td>
                      <td className="text-center">
                        {item.orderCount.toLocaleString("en-IN")}
                      </td>
                      <td className="text-center">
                        {item.totalOrderValue.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}

                  {/* Totals */}
                  <tr className="table-success fw-bold">
                    <td colSpan={2} className="text-end">
                      Total
                    </td>
                    <td className="text-center">
                      {totalOrdersCount.toLocaleString("en-IN")}
                    </td>
                    <td className="text-center">
                      {totalOrdersValue.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ===== Invoices/Bills Summary ===== */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="card-title mb-0">2. Invoices / Bills Summary</h5>
          </div>
          <div className="card-body table-responsive p-2">
            {invoicesSummary.length === 0 ? (
              <p className="text-muted text-center m-0">
                No invoices summary data found.
              </p>
            ) : (
              <table className="table table-bordered table-hover table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Sales Officer</th>
                    <th className="text-center">Total Invoice Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesSummary.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.salesOfficerName}</td>
                      <td className="text-center">
                        {item.totalAmount.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}

                  {/* Totals */}
                  <tr className="table-success fw-bold">
                    <td className="text-end">Total</td>
                    <td className="text-center">
                      {totalInvoiceAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ===== Receipts / Collection Summary ===== */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-warning text-dark">
            <h5 className="card-title mb-0">
              3. Receipts / Collection Summary
            </h5>
          </div>
          <div className="card-body table-responsive p-2">
            {reciptsCollectionSummary.length === 0 ? (
              <p className="text-muted text-center m-0">
                No receipts / collection data found.
              </p>
            ) : (
              <table className="table table-bordered table-hover table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Sales Officer</th>
                    <th className="text-center">Total Receipt Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reciptsCollectionSummary.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.salesOfficerName}</td>
                      <td className="text-center">
                        {item.totalAmount.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}

                  {/* Totals */}
                  <tr className="table-success fw-bold">
                    <td className="text-end">Total</td>
                    <td className="text-center">
                      {totalReceiptsAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSummaryReport_BusinessActivities;
